import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, FileJson, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { useBrokers } from "@/context/BrokersContext";
import { parseBrokerUpload } from "@/lib/brokerSchema";
import "./admin.css";

export default function AdminBrokers() {
  const { brokers, customBrokers, seedCount, upsertCustomBroker, removeCustomBroker } = useBrokers();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [parseError, setParseError] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json") && file.type !== "application/json") {
      setParseError("Please upload a .json file.");
      setPreview(null);
      return;
    }
    const text = await file.text();
    const result = parseBrokerUpload(text);
    if (!result.ok) {
      setParseError(result.error);
      setPreview(null);
      return;
    }
    setParseError("");
    setPreview(result.broker);
  }, []);

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    await onFile(e.dataTransfer.files?.[0]);
  };

  const publish = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const saved = await upsertCustomBroker(preview);
      toast.success(`Broker “${saved.name}” published`);
      setPreview(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      toast.error(err.message || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug, name) => {
    if (!window.confirm(`Remove custom broker “${name}”? Seed brokers cannot be deleted.`)) return;
    try {
      await removeCustomBroker(slug);
      toast.success(`Removed ${name}`);
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-brokers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="admin-mono text-[11px] text-[#64748b]">Catalog</p>
          <h1 className="mt-1 text-[36px] font-light tracking-[-0.045em] text-white">Brokers</h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#94a3b8]">
            Upload a full broker JSON to create or replace a catalog entry. Published brokers appear across finder, directory, and search.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="admin-panel px-4 py-3">
            <p className="admin-mono text-[10px] text-[#64748b]">Live catalog</p>
            <p className="text-xl font-medium tracking-[-0.03em] text-white">{brokers.length}</p>
          </div>
          <div className="admin-panel px-4 py-3">
            <p className="admin-mono text-[10px] text-[#64748b]">Seed / custom</p>
            <p className="text-xl font-medium tracking-[-0.03em] text-white">{seedCount} / {customBrokers.length}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-panel p-6"
        >
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-medium tracking-[-0.02em] text-white">Upload broker JSON</h2>
              <p className="mt-1 text-sm text-[#94a3b8]">Create or replace a full broker record.</p>
            </div>
            <a href="/admin/broker-template.json" download className="admin-btn admin-btn-ghost !py-2 !px-3 text-sm">
              <Download size={14} /> Template
            </a>
          </div>

          <div
            className="admin-dropzone"
            data-active={dragOver}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
            data-testid="admin-json-dropzone"
          >
            <UploadCloud className="mx-auto mb-3 text-[#0052ff]" size={28} />
            <p className="text-sm font-medium text-white">Drop broker JSON here</p>
            <p className="mt-1 text-xs text-[#64748b]">or click to browse · .json only</p>
            <input
              ref={inputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              data-testid="admin-json-input"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </div>

          {parseError ? (
            <p className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" role="alert">
              {parseError}
            </p>
          ) : null}

          {preview ? (
            <div className="mt-5 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#0052ff]/25 bg-[#0052ff]/10 px-4 py-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 text-[#60a5fa]" size={18} />
                  <div>
                    <p className="text-sm font-medium text-white">{preview.name}</p>
                    <p className="admin-mono text-[11px] text-[#93c5fd]">
                      {preview.slug} · trust {preview.trust} · {preview.spreadModel}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="admin-btn admin-btn-primary !py-2"
                  disabled={busy}
                  onClick={publish}
                  data-testid="admin-publish-broker"
                >
                  <FileJson size={14} />
                  {busy ? "Publishing…" : "Publish broker"}
                </button>
              </div>
              <pre className="admin-preview admin-mono" data-testid="admin-json-preview">
                {JSON.stringify(preview, null, 2)}
              </pre>
            </div>
          ) : null}
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="admin-panel p-6"
        >
          <h2 className="text-lg font-medium tracking-[-0.02em] text-white">Custom / overridden</h2>
          <p className="mt-1 text-sm text-[#94a3b8]">New brokers and seed brokers with updated data.</p>

          <div className="mt-5 overflow-x-auto">
            {customBrokers.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 px-4 py-10 text-center text-sm text-[#64748b]">
                No custom overrides yet.
              </div>
            ) : (
              <table className="admin-table" data-testid="admin-custom-brokers-table">
                <thead>
                  <tr>
                    <th>Broker</th>
                    <th>Trust</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {customBrokers.map((b) => (
                    <tr key={b.slug}>
                      <td>
                        <p className="text-sm text-white">{b.name}</p>
                        <p className="admin-mono text-[11px] text-[#64748b]">{b.slug}</p>
                      </td>
                      <td className="admin-mono text-sm text-[#cbd5e1]">{b.trust}</td>
                      <td className="text-right">
                        <div className="flex justify-end gap-2">
                          <Link to={`/brokers/${b.slug}`} className="admin-btn admin-btn-ghost !px-2.5 !py-1.5 text-xs" target="_blank" rel="noreferrer">
                            View
                          </Link>
                          <button
                            type="button"
                            className="admin-btn admin-btn-danger !px-2.5 !py-1.5 text-xs"
                            onClick={() => remove(b.slug, b.name)}
                            data-testid={`admin-delete-${b.slug}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </motion.section>
      </div>
    </div>
  );
}
